import Cancel from "@mui/icons-material/Cancel";
import CheckCircle from "@mui/icons-material/CheckCircle";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import { Chip, Stack, Tooltip, Typography } from "@mui/material";
import { green, red, orange, grey, blue } from "@mui/material/colors";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  CredentialImplementationsForStaffQuery,
  ImplementationStatus,
  CredentialVerificationStatus,
} from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { prettyPrintDateTimeMed } from "util/dates";

export type Row =
  CredentialImplementationsForStaffQuery["credentialImplementationsForStaff"]["items"][number];

const IMPLEMENTATION_STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  [ImplementationStatus.NotStarted]: { bg: grey[300], text: "#000" },
  [ImplementationStatus.InProgress]: { bg: blue[100], text: blue[800] },
  [ImplementationStatus.Blocked]: { bg: red[100], text: red[800] },
  [ImplementationStatus.Complete]: { bg: green[100], text: green[800] },
  [ImplementationStatus.NotApplicable]: { bg: grey[200], text: grey[600] },
};

const CREDENTIAL_STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  [CredentialVerificationStatus.Good]: { bg: green[100], text: green[800] },
  [CredentialVerificationStatus.NotAttempted]: { bg: grey[200], text: grey[700] },
  [CredentialVerificationStatus.CurrentlyVerifying]: { bg: blue[100], text: blue[800] },
  [CredentialVerificationStatus.InUse]: { bg: orange[100], text: orange[800] },
  // Hard errors: credential is broken and needs human intervention to fix.
  [CredentialVerificationStatus.BadUsername]: { bg: red[100], text: red[800] },
  [CredentialVerificationStatus.BadPassword]: { bg: red[100], text: red[800] },
  [CredentialVerificationStatus.InvalidAccessidOrPassword]: { bg: red[100], text: red[800] },
  [CredentialVerificationStatus.BadSecurityQuestionAnswer]: { bg: red[100], text: red[800] },
  [CredentialVerificationStatus.UnknownNonVerifiable]: { bg: red[100], text: red[800] },
  [CredentialVerificationStatus.UnknownErrorNeedsHuman]: { bg: red[100], text: red[800] },
  // Recoverable / action-required states.
  [CredentialVerificationStatus.ExpiredPassword]: { bg: orange[100], text: orange[800] },
  [CredentialVerificationStatus.Locked]: { bg: orange[100], text: orange[800] },
  [CredentialVerificationStatus.RequiresNewPassword]: { bg: orange[100], text: orange[800] },
  [CredentialVerificationStatus.FailedOneTimePassword]: { bg: orange[100], text: orange[800] },
};

function BooleanIcon({ value }: { value: boolean }) {
  return value ? (
    <CheckCircle sx={{ color: green[600], fontSize: 20 }} />
  ) : (
    <Cancel sx={{ color: red[300], fontSize: 20 }} />
  );
}

export const COLUMNS: OmnisearchGridColDef<Row>[] = [
  {
    field: "tenantName",
    headerName: "Tenant",
    width: 180,
    filterKeyType: "enumAutocomplete",
  },
  {
    field: "state",
    headerName: "State",
    width: 80,
    filterKeyType: "text",
  },
  {
    field: "portalName",
    headerName: "Portal",
    width: 160,
  },
  {
    field: "facilityName",
    headerName: "Facility",
    width: 220,
    renderCell: ({ row }) => (
      <Tooltip title={row.facilityAddress ?? ""}>
        <Typography variant="body2" noWrap>
          {row.facilityName ?? "—"}
        </Typography>
      </Tooltip>
    ),
  },
  {
    field: "county",
    headerName: "County",
    width: 120,
  },
  {
    field: "credentialName",
    headerName: "Credential Name",
    width: 160,
  },
  {
    field: "credentialEmail",
    headerName: "Credential Email",
    width: 180,
  },
  {
    field: "credentialInEncamp",
    headerName: "Credential in Encamp?",
    width: 160,
    align: "center",
    headerAlign: "center",
    renderCell: ({ row }) => <BooleanIcon value={row.credentialInEncamp} />,
  },
  {
    field: "implementationStatus",
    headerName: "Implementation Status",
    width: 180,
    filterKeyType: "enum",
    enumValues: Object.values(ImplementationStatus),
    enumPresentationFunction: prettyPrintEnumValue,
    renderCell: ({ row }) => {
      const colors = IMPLEMENTATION_STATUS_COLORS[row.implementationStatus] ?? {
        bg: grey[200],
        text: "#000",
      };
      return (
        <Chip
          label={prettyPrintEnumValue(row.implementationStatus, [], true)}
          size="small"
          sx={{ backgroundColor: colors.bg, color: colors.text }}
        />
      );
    },
  },
  {
    field: "credentialStatus",
    headerName: "Credential Status",
    width: 180,
    filterKeyType: "enum",
    enumValues: Object.values(CredentialVerificationStatus),
    enumPresentationFunction: prettyPrintEnumValue,
    renderCell: ({ row }) => {
      if (!row.credentialStatus) return "—";
      const colors = CREDENTIAL_STATUS_COLORS[row.credentialStatus] ?? {
        bg: grey[200],
        text: "#000",
      };
      return (
        <Chip
          label={prettyPrintEnumValue(row.credentialStatus)}
          size="small"
          sx={{ backgroundColor: colors.bg, color: colors.text }}
        />
      );
    },
  },
  {
    field: "lastVerifiedAt",
    headerName: "Last Verified",
    width: 160,
    renderCell: ({ row }) =>
      row.lastVerifiedAt ? prettyPrintDateTimeMed(row.lastVerifiedAt) : "—",
  },
  {
    field: "lockHolder",
    headerName: "Currently In Use",
    width: 130,
    align: "center",
    headerAlign: "center",
    renderCell: ({ row }) => {
      if (!row.lockHolder) return "";
      const expiry = row.lockExpiresAt ? prettyPrintDateTimeMed(row.lockExpiresAt) : "";
      const tooltipText = expiry ? `${row.lockHolder} (expires ${expiry})` : row.lockHolder;
      return (
        <Tooltip title={tooltipText}>
          <LockOutlined sx={{ color: orange[700], fontSize: 20 }} />
        </Tooltip>
      );
    },
  },
  {
    field: "seenInPortal",
    headerName: "Facility in Portal",
    description:
      "Indicates whether this facility's state ID was seen in the portal during the last credential verification check",
    width: 140,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <Tooltip
        title="Indicates whether this facility's state ID was seen in the portal during the last credential verification check"
        placement="top"
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <span>Facility in Portal</span>
          <InfoOutlined sx={{ fontSize: 14, color: "text.secondary" }} />
        </Stack>
      </Tooltip>
    ),
    renderCell: ({ row }) => <BooleanIcon value={row.seenInPortal} />,
  },
  {
    field: "stateId",
    headerName: "State ID in Encamp",
    width: 160,
    renderCell: ({ row }) => {
      if (!row.stateId) return <Typography color="text.secondary">Missing</Typography>;
      return (
        <Tooltip title={row.stateIdType ?? ""}>
          <Typography variant="body2" noWrap>
            {row.stateId}
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    field: "csmName",
    headerName: "CSM",
    width: 140,
    renderCell: ({ row }) => {
      if (!row.csmName) return "—";
      return (
        <Tooltip title={row.csmEmail ?? ""}>
          <Typography variant="body2" noWrap>
            {row.csmName}
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    field: "assigneeName",
    headerName: "Assignee",
    width: 140,
    renderCell: ({ row }) => {
      if (!row.assigneeName) return "—";
      return (
        <Tooltip title={row.assigneeEmail ?? ""}>
          <Typography variant="body2" noWrap>
            {row.assigneeName}
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    field: "mostRecentComment",
    headerName: "Most Recent Comment",
    width: 200,
    sortable: false,
    renderCell: ({ row }) => {
      if (!row.mostRecentComment) return "";
      const dateStr = row.mostRecentCommentAt
        ? prettyPrintDateTimeMed(row.mostRecentCommentAt)
        : "";
      return (
        <Tooltip title={`${dateStr}: ${row.mostRecentComment}`}>
          <Typography variant="body2" noWrap>
            {row.mostRecentComment}
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    field: "notes",
    headerName: "Notes",
    width: 180,
    sortable: false,
    renderCell: ({ row }) => {
      const text = row.blockedReason ? `[Blocked] ${row.blockedReason}` : row.notes;
      if (!text) return "";
      return (
        <Tooltip title={text}>
          <Typography variant="body2" noWrap>
            {text}
          </Typography>
        </Tooltip>
      );
    },
  },
];
